import type { LoadingState } from '@ipfabric/ui-components/utils/loading/withLoadingIndicator';
import { type TableColumn, getValueFromKeyOfType } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { useTableContextSelectors } from 'components/Table/model/TableModel';
import { selectTableCellContext } from 'components/Table/model/selectors/selectTableCellContext';
import { selectVisibleColumns } from 'components/Table/model/selectors/selectVisibleColumns';
import { useEffect, useRef, useState } from 'react';
import { useApi } from 'utils/request/useApi';
import { loadInterfaceDetailData } from './interfaceDetail';

export type RowDetailData = {
  loadingState: LoadingState;
  item: unknown | null;
  columns: TableColumn<any>[];
};

type RowDetailDataHook = {
  columnDetailData?: Record<string, RowDetailData>;
};

// Note: explicitly typed as unknown | undefined since we expect unknown row data or
// undefined if no data is loaded
export const useRowDetailData = (
  row: unknown | undefined,
): RowDetailDataHook => {
  const ctxProps = useTableContextSelectors({
    dispatch: prop('dispatch'),
    tableRowDetail: prop('tableRowDetail'),
    columns: selectVisibleColumns,
    data: prop('data'),
    tableCellContext: selectTableCellContext,
    snapshotId: prop('snapshotId'),
  });
  const api = useApi();
  const [columnDetailData, setColumnDetailData] = useState<
    Record<string, RowDetailData>
  >({});
  const controllerRef = useRef<AbortController>();

  useEffect(() => {
    if (!row || ctxProps.snapshotId === null) {
      return;
    }

    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    const currentController = new AbortController();
    controllerRef.current = currentController;

    ctxProps.columns.map(async (column) => {
      if (column.format.type === 'interface') {
        setColumnDetailData((s) => ({
          ...s,
          [column.key]: {
            loadingState: 'LOADING',
            item: null,
            columns: [],
          },
        }));

        const rowDetailData = await loadInterfaceDetailData(
          api,
          ctxProps.snapshotId!,
          getValueFromKeyOfType<any, string>(row, column.format.field),
          getValueFromKeyOfType<any, string>(row, column.format.snField),
          controllerRef.current?.signal,
        );
        if (rowDetailData !== null) {
          setColumnDetailData((s) => ({
            ...s,
            [column.key]: rowDetailData,
          }));
        }
      }
    });
  }, [row, ctxProps.snapshotId, ctxProps.columns]);

  return {
    columnDetailData,
  };
};
